import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress, IconButton, ListItemText, Menu, MenuItem, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { FormatoExportacion } from "../../types/Jornada";
import * as constantes from "../../common/Constantes";

// Botón «Exportar» del mes: PDF, CSV de resumen diario (para la gestoría) o CSV completo (todos los fichajes, también los
// corregidos, anulados y deshechos). En Registros, de la persona elegida o de todo el equipo; en Mis fichajes, lo propio.
interface ExportarJornadaProps {
    mes: string;
    propio?: boolean;        // Mis fichajes: lo de quien exporta, aunque sea admin
    especialistaId?: number; // Registros: la persona elegida; sin ella, todo el equipo
    setAlert: (alert: IAlert) => void;
}

type Fallo = { message?: string; response?: { status?: number; data?: unknown } };

const OPCIONES: { formato: FormatoExportacion; titulo: string; texto: string }[] = [
    { formato: 'pdf', titulo: 'PDF', texto: 'Para archivar o enseñar en una inspección' },
    { formato: 'resumen', titulo: 'CSV resumen', texto: 'Una fila por día, para la gestoría' },
    { formato: 'completo', titulo: 'CSV completo', texto: 'Todos los fichajes, también los corregidos y anulados' },
];

// Nombre del fichero que manda la API (Content-Disposition)
const nombreDe = (cabecera: string | undefined, porDefecto: string) => cabecera?.match(/filename="?([^";]+)"?/)?.[1] ?? porDefecto;

const ExportarJornada = ({ mes, propio = false, especialistaId, setAlert }: ExportarJornadaProps) => {
    const history = useNavigate();
    const [ancla, setAncla] = useState<HTMLElement | null>(null);
    const [descargando, setDescargando] = useState(false);
    const esMovil = useMediaQuery(useTheme().breakpoints.down('sm'));

    const descargar = (formato: FormatoExportacion) => {
        setAncla(null);
        setDescargando(true);
        JornadaService.exportar(mes, formato, propio ? { propio: true } : { especialistaId })
            .then(response => {
                const extension = formato === 'pdf' ? 'pdf' : 'csv';
                const url = window.URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = nombreDe(String(response.headers['content-disposition'] ?? ''), `registro-jornada_${mes}.${extension}`);
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(async (e: unknown) => {
                const fallo = e as Fallo;
                if (fallo.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                    return;
                }
                // Con responseType blob, el mensaje de la API llega dentro de un Blob
                let mensaje = '';
                if (fallo.response?.data instanceof Blob) {
                    try { mensaje = (JSON.parse(await fallo.response.data.text()) as { message?: string }).message ?? ''; } catch { mensaje = ''; }
                }
                if (![400, 403, 404].includes(fallo.response?.status ?? 0)) {
                    const error: ILog = { method: 'ExportarJornada.exportar', level: 'error', message: fallo.message ?? '', meta: fallo.response?.status };
                    LogService.logError(error);
                }
                setAlert({ open: true, severity: 'error', msg: mensaje || 'No se ha podido exportar, vuelve a intentarlo' });
            })
            .finally(() => setDescargando(false));
    };

    return (
        <>
            {/* En móvil, solo el icono: el botón con texto no cabe junto al selector de mes */}
            {esMovil ? (
                <Tooltip title="Exportar">
                    <span>
                        <IconButton color="primary" aria-label="Exportar" disabled={descargando} onClick={(e) => setAncla(e.currentTarget)}>
                            {descargando ? <CircularProgress size={20} /> : <DownloadIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            ) : (
                <Button size="small" variant="outlined" disabled={descargando} startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={(e) => setAncla(e.currentTarget)}>
                    Exportar
                </Button>
            )}
            <Menu anchorEl={ancla} open={ancla !== null} onClose={() => setAncla(null)}>
                {OPCIONES.map(o => (
                    <MenuItem key={o.formato} onClick={() => descargar(o.formato)}>
                        <ListItemText primary={o.titulo} secondary={o.texto} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ExportarJornada;
